import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell } from "lucide-react";

const DISMISS_KEY = "push_prompt_dismissed_at";
const COOLDOWN_DAYS = 7;

/**
 * On login, if user is admin or gouvernante and push notifications are not
 * granted/subscribed, prompts to enable them. Dismissal is remembered for 7 days.
 */
export default function PushPromptOnLogin() {
  const { user, userRole } = useAuth();
  const { isSupported, isSubscribed, permission, subscribe, isLoading } = usePushNotifications();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || isLoading) return;
    if (userRole !== "admin" && userRole !== "gouvernante") return;
    if (!isSupported) return;
    if (permission === "granted" && isSubscribed) return;
    if (permission === "denied") return; // Can't reprompt
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const days = (Date.now() - Number(dismissed)) / (1000 * 60 * 60 * 24);
      if (days < COOLDOWN_DAYS) return;
    }
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user, userRole, isLoading, isSupported, isSubscribed, permission]);

  const handleEnable = async () => {
    const ok = await subscribe();
    if (ok) setOpen(false);
  };

  const handleLater = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleLater(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" /> Activer les notifications
          </DialogTitle>
          <DialogDescription>
            Recevez en temps réel les alertes importantes (devis, paiements, check-in, messages...).
            Vous pourrez les désactiver à tout moment depuis la page Système.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={handleLater}>Plus tard</Button>
          <Button onClick={handleEnable}>Activer maintenant</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
